import React, { useEffect, useState, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { 
  Box, 
  Typography, 
  TextField, 
  Button, 
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  Divider,
  CircularProgress,
  Autocomplete,
  Paper,
  IconButton,
} from '@mui/material';
import { Send as SendIcon } from '@mui/icons-material';
// Removed Redux auth selectors - now using useAuth() hook
import { fetchAvailableUsers } from '../../redux/slices/usersSlice';
import { fetchThreadMessages } from '../../redux/slices/messagingSlice';
import { 
  getCurrentUserData, 
  subscribeToThreadUpdates, 
  addParticipantsToThread, 
  addSystemMessage,
  sendMessage as sendMessageService
} from '../../services/messagingService';
import './ThreadDetails.css';
import { useUserData } from '../../hooks/useUserData';

const ThreadDetails = React.memo(({ threadId }) => {
  const dispatch = useDispatch();
  // const role = useAuth().user?.role; // Now using useAuth() hook
const { user: currentUser } = useUserData();
  
  const [newMessage, setNewMessage] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedParticipants, setSelectedParticipants] = useState([]);
  const [messages, setMessages] = useState([]);
  const [thread] = useState(null);
  const [userData, setUserData] = useState(null);
  const messagesEndRef = useRef(null);
  const [loading, setLoading] = useState(true);
  
  // Fetch complete user data
  useEffect(() => {
    const fetchUserData = async () => {
      if (!currentUser?.uid) return;
      
      try {
        const userDataResult = await getCurrentUserData(currentUser.uid);
        setUserData(userDataResult);
      } catch (error) {
        console.error('Error fetching user data:', error);
      }
    };

    fetchUserData();
  }, [currentUser?.uid]);

  // Debug logs
  console.log('Current user:', currentUser);
  console.log('User data:', userData);
  // Handle both single role string and array of roles
  const userRoles = currentUser.role || [];
  const roles = Array.isArray(userRoles) ? userRoles : [userRoles];
  const roleStrings = roles.map(role => String(role).toLowerCase());
  const canManageParticipants = ['admin', 'doctor', 'nurse'].some(providerRole => 
    roleStrings.includes(providerRole)
  );
  console.log('Can manage participants:', canManageParticipants);

  const currentThread = useSelector(state => state.messaging.currentThread);
  const availableUsers = useSelector(state => state.users.availableUsers);

  useEffect(() => {
    dispatch(fetchAvailableUsers());
    dispatch(fetchThreadMessages(threadId));
  }, [dispatch, threadId]);

  //const canManageParticipants = ['admin', 'doctor', 'nurse'].includes(currentUser.role);

  // Filter out users who are already participants
  const availableParticipants = availableUsers.filter(user => 
    !currentThread?.participants?.some(p => p.id === user.id)
  );

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    console.log('🔍 ThreadDetails useEffect triggered with threadId:', {
      threadId,
      timestamp: new Date().toISOString()
    });

    if (!threadId) {
      console.log('❌ ThreadDetails: No threadId, skipping listener setup');
      return;
    }

    console.log('📡 ThreadDetails: Setting up thread and messages listeners...');

    // Subscribe to thread updates using service layer
    const cleanupThread = subscribeToThreadUpdates(threadId, (update) => {
      console.log('📨 ThreadDetails: Thread update received:', {
        timestamp: new Date().toISOString(),
        messageCount: update.messages?.length || 0
      });
      
      if (update.messages) {
        setMessages(update.messages);
        setLoading(false);
        scrollToBottom();
      }
    });

    // Also fetch initial data
    const fetchInitialData = async () => {
      try {
        const messagesData = await dispatch(fetchThreadMessages(threadId));
        if (messagesData.payload) {
          setMessages(messagesData.payload);
          setLoading(false);
          scrollToBottom();
        }
      } catch (error) {
        console.error('Error fetching initial data:', error);
        setLoading(false);
      }
    };

    fetchInitialData();

    console.log('✅ ThreadDetails: Listeners setup complete, returning cleanup function');
    return () => {
      console.log('🧹 ThreadDetails: Cleaning up thread listeners');
      cleanupThread();
    };
  }, [threadId, dispatch]);

  const handleAddParticipants = async () => {
    try {
      const participantIds = selectedParticipants.map(p => p.id);
      await addParticipantsToThread(threadId, participantIds);

      const participantNames = selectedParticipants.map(p => 
        p.firstName && p.lastName ? `${p.firstName} ${p.lastName}` : p.email
      ).join(', ');
      
      await addSystemMessage(threadId, `Added participants: ${participantNames}`);

      setSelectedParticipants([]);
      setOpenDialog(false);
      dispatch(fetchThreadMessages(threadId));
    } catch (error) {
      console.error('Error adding participants:', error);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !userData) return;

    try {
      // Use userData for sender name
      const senderName = userData.firstName && userData.lastName 
        ? `${userData.firstName} ${userData.lastName}`
        : userData.email || currentUser.email;

      await sendMessageService({
        threadId,
        content: newMessage.trim(),
        senderId: currentUser.uid,
        senderName
      });

      setNewMessage('');
    } catch (error) {
      console.error('Error sending message:', error);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate();
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric'
    }).format(date);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box className="thread-details-container">
      {thread && (
        <Box className="thread-header">
          <Typography variant="h6">
            {thread.subject || 'Conversation'}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {thread.participantDetails
              .filter(p => p.id !== currentUser.uid)
              .map(p => p.name)
              .join(', ')}
          </Typography>
        </Box>
      )}

      <Paper className="messages-container">
        <List>
          {messages.map((message, index) => (
            <React.Fragment key={message.id}>
              <ListItem className={message.sentBy === currentUser.uid ? 'sent' : 'received'}>
                <Box className="message-content">
                  {message.type !== 'system' && (
                    <Typography variant="caption" className="sender-name">
                      {message.senderName}
                    </Typography>
                  )}
                  <Paper className={`message-bubble ${message.type === 'system' ? 'system' : ''}`}>
                    <Typography>{message.content}</Typography>
                  </Paper>
                  <Typography variant="caption" className="message-time">
                    {formatDate(message.sentAt)}
                  </Typography>
                </Box>
              </ListItem>
              {index < messages.length - 1 && <Divider variant="middle" />}
            </React.Fragment>
          ))}
          <div ref={messagesEndRef} />
        </List>
      </Paper>

      <Box component="form" onSubmit={handleSendMessage} className="message-input-container">
        <TextField
          fullWidth
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          placeholder="Type your message..."
          variant="outlined"
          size="small"
        />
        <IconButton 
          type="submit" 
          color="primary" 
          disabled={!newMessage.trim()}
          className="send-button"
        >
          <SendIcon />
        </IconButton>
      </Box>

      {/* Add Participants Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
        <DialogTitle>Add Participants</DialogTitle>
        <DialogContent>
          <Autocomplete
            multiple
            options={availableParticipants || []}
            getOptionLabel={(option) => 
              option.firstName && option.lastName 
                ? `${option.firstName} ${option.lastName} (${option.role})`
                : option.email
            }
            value={selectedParticipants}
            onChange={(_, newValue) => setSelectedParticipants(newValue)}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                label="Select Participants"
                fullWidth
                sx={{ mt: 2 }}
              />
            )}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button 
            onClick={handleAddParticipants}
            disabled={selectedParticipants.length === 0}
            variant="contained"
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
      );
});

export default ThreadDetails;
